import { HttpErrorResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { JsonPatchOperationModel, TruckDocumentDto } from '@fleet-beat/api';
import {
  AuthHeaders,
  BaseApiService,
  CoreInjectorTokens,
  DocumentDto,
  Environment,
  GetManyDto,
  GetManyV2Dto,
  GetOneDto,
  PageableListDto,
  PageableListV2Dto,
  ResponseDto,
} from '@fleet-beat/core';
import { Observable, catchError, map } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class TruckDocumentsApiService {
  private readonly baseUrl: string = `${this.environment.apiEndpoint}/truck-documents`;

  constructor(
    @Inject(CoreInjectorTokens.Environment) private environment: Environment,
    private baseApiService: BaseApiService,
  ) {}

  getMany(params: GetManyDto, auth: AuthHeaders): Observable<PageableListDto<DocumentDto>> {
    return this.baseApiService.get(this.baseUrl, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<PageableListDto<DocumentDto>>) => this.baseApiService.mapResponse(response)),
    );
  }

  getManyV2(params: GetManyV2Dto, auth: AuthHeaders): Observable<PageableListV2Dto<DocumentDto>> {
    return this.baseApiService.get(this.baseUrl, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DocumentDto[]>) => this.baseApiService.mapResponseV2(response)),
      map((response: ResponseDto<DocumentDto[]>) => {
        return {
          data: response.data,
          ...response.meta,
        };
      }),
    );
  }

  getOne(id: string, auth: AuthHeaders, params: GetOneDto): Observable<DocumentDto> {
    return this.baseApiService.get(`${this.baseUrl}/${id}`, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DocumentDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  postOne(data: TruckDocumentDto, auth: AuthHeaders): Observable<DocumentDto> {
    return this.baseApiService.post(this.baseUrl, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DocumentDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  delete(id: string, auth: AuthHeaders): Observable<DocumentDto> {
    return this.baseApiService.delete(`${this.baseUrl}/${id}`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DocumentDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  patchOne(id: string, operation: JsonPatchOperationModel, auth: AuthHeaders): Observable<DocumentDto> {
    return this.baseApiService.patch(`${this.baseUrl}/${id}`, [operation], { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DocumentDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  patchOneV2(id: string, data: DocumentDto, auth: AuthHeaders): Observable<DocumentDto> {
    return this.baseApiService.patch(`${this.baseUrl}/${id}`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DocumentDto>) => this.baseApiService.mapResponse(response)),
    );
  }

  getManyByIds(ids: string[], include: string, auth: AuthHeaders): Observable<DocumentDto[]> {
    return this.baseApiService.post(`${this.baseUrl}/GetUserDocumentsByIDs`, ids, { auth, params: { include } }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<DocumentDto[]>) => this.baseApiService.mapResponse(response)),
    );
  }
}
